import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getClients, getProducts, getSettings, createQuote } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { ArrowLeft, Plus, Trash2, CalendarIcon, Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const CreateQuotePage = () => {
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [clientId, setClientId] = useState('');
  const [currency, setCurrency] = useState('CHF');
  const [vatEnabled, setVatEnabled] = useState(true);
  const [validUntil, setValidUntil] = useState(null);
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState([{ description: '', quantity: 1, unit_price: 0, vat_rate: 7.7, discount_percent: 0 }]);
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [clientsRes, productsRes, settingsRes] = await Promise.all([getClients(), getProducts(), getSettings()]);
      setClients(clientsRes.data);
      setProducts(productsRes.data);
      setSettings(settingsRes.data);
      setCurrency(settingsRes.data.default_currency || 'CHF');
      setVatEnabled(settingsRes.data.vat_enabled !== false);
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setLoading(false);
    }
  };

  const addItem = () => {
    setItems([...items, { description: '', quantity: 1, unit_price: 0, vat_rate: settings?.default_vat_rate || 7.7, discount_percent: 0 }]);
  };

  const addProductItem = (product) => {
    setItems([...items, {
      description: product.name + (product.description ? ` - ${product.description}` : ''),
      quantity: 1,
      unit_price: product.unit_price,
      vat_rate: product.vat_rate,
      discount_percent: 0,
      product_id: product.id
    }]);
  };

  const removeItem = (index) => {
    if (items.length > 1) setItems(items.filter((_, i) => i !== index));
  };

  const updateItem = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = field === 'description' ? value : parseFloat(value) || 0;
    setItems(newItems);
  };

  const calculateSubtotal = () => {
    return items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
  };

  const calculateDiscount = () => {
    return items.reduce((sum, item) => {
      const lineSubtotal = item.quantity * item.unit_price;
      return sum + (lineSubtotal * (item.discount_percent / 100));
    }, 0);
  };

  const calculateVat = () => {
    if (!vatEnabled) return 0;
    return items.reduce((sum, item) => {
      const lineSubtotal = item.quantity * item.unit_price;
      const afterDiscount = lineSubtotal - (lineSubtotal * (item.discount_percent / 100));
      return sum + (afterDiscount * item.vat_rate / 100);
    }, 0);
  };

  const calculateTotal = () => {
    return calculateSubtotal() - calculateDiscount() + calculateVat();
  };

  const formatCurrency = (amount) => new Intl.NumberFormat('fr-CH', { style: 'currency', currency }).format(amount);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!clientId) { toast.error('Sélectionnez un client'); return; }
    if (items.some(item => !item.description)) { toast.error('Remplissez tous les articles'); return; }
    
    setSaving(true);
    try {
      const response = await createQuote({
        client_id: clientId,
        items,
        currency,
        vat_enabled: vatEnabled,
        valid_until: validUntil ? format(validUntil, 'yyyy-MM-dd') : null,
        notes: notes || null,
        status: 'draft'
      });
      toast.success(`Devis ${response.data.quote_number} créé`);
      navigate('/quotes');
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>;
  }

  return (
    <div className="space-y-6" data-testid="create-quote-page">
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/quotes')}><ArrowLeft className="w-4 h-4 mr-2" />Retour</Button>
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Nouveau devis</h1>
          <p className="text-slate-500">Créez un devis pour un client</p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {/* Client & Currency & VAT */}
            <Card className="border-slate-200">
              <CardHeader><CardTitle className="font-heading text-lg">Client, Devise & TVA</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Client *</Label>
                    <Select value={clientId} onValueChange={(v) => {
                        setClientId(v);
                        const sel = clients.find(c => c.id === v);
                        if (sel?.default_currency) setCurrency(sel.default_currency);
                      }}>
                      <SelectTrigger data-testid="client-select"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                      <SelectContent>
                        {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Devise</Label>
                    <Select value={currency} onValueChange={setCurrency}>
                      <SelectTrigger data-testid="currency-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="CHF">CHF - Franc suisse</SelectItem>
                        <SelectItem value="EUR">EUR - Euro</SelectItem>
                        <SelectItem value="USD">USD - Dollar américain</SelectItem>
                        <SelectItem value="GBP">GBP - Livre sterling</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                {/* TVA Toggle */}
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <div>
                    <p className="font-medium text-slate-900">Avec TVA</p>
                    <p className="text-sm text-slate-500">Cochez pour inclure la TVA sur ce devis</p>
                  </div>
                  <Switch 
                    checked={vatEnabled} 
                    onCheckedChange={setVatEnabled}
                    data-testid="vat-toggle"
                  />
                </div>
                
                {!vatEnabled && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-sm text-amber-800">
                      <strong>Sans TVA</strong> - La mention "Non assujetti à la TVA" apparaîtra sur le devis PDF.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Items */}
            <Card className="border-slate-200">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="font-heading text-lg">Articles</CardTitle>
                <div className="flex gap-2">
                  {products.length > 0 && (
                    <Select onValueChange={(productId) => {
                      const product = products.find(p => p.id === productId);
                      if (product) addProductItem(product);
                    }}>
                      <SelectTrigger className="w-48">
                        <SelectValue placeholder="+ Depuis catalogue" />
                      </SelectTrigger>
                      <SelectContent>
                        {products.map((product) => (
                          <SelectItem key={product.id} value={product.id}>
                            {product.name} ({formatCurrency(product.unit_price)})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  <Button type="button" variant="outline" size="sm" onClick={addItem} data-testid="add-item-btn">
                    <Plus className="w-4 h-4 mr-2" />Ligne vide
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {items.map((item, index) => (
                    <div key={index} className="grid grid-cols-12 gap-3 items-start p-4 bg-slate-50 rounded-lg">
                      <div className={vatEnabled ? "col-span-12 sm:col-span-4" : "col-span-12 sm:col-span-5"}>
                        <Label className="text-xs text-slate-500">Description</Label>
                        <Input value={item.description} onChange={(e) => updateItem(index, 'description', e.target.value)} data-testid={`item-description-${index}`} />
                      </div>
                      <div className="col-span-3 sm:col-span-2">
                        <Label className="text-xs text-slate-500">Qté</Label>
                        <Input type="number" min="0.01" step="0.01" value={item.quantity} onChange={(e) => updateItem(index, 'quantity', e.target.value)} />
                      </div>
                      <div className="col-span-4 sm:col-span-2">
                        <Label className="text-xs text-slate-500">Prix unit.</Label>
                        <Input type="number" min="0" step="0.01" value={item.unit_price} onChange={(e) => updateItem(index, 'unit_price', e.target.value)} onFocus={(e) => e.target.select()} />
                      </div>
                      <div className="col-span-3 sm:col-span-2">
                        <Label className="text-xs text-slate-500">Remise %</Label>
                        <Input type="number" min="0" max="100" step="0.1" value={item.discount_percent} onChange={(e) => updateItem(index, 'discount_percent', e.target.value)} onFocus={(e) => e.target.select()} />
                      </div>
                      {vatEnabled && (
                        <div className="col-span-2 sm:col-span-1">
                          <Label className="text-xs text-slate-500">TVA %</Label>
                          <Input type="number" min="0" max="100" step="0.1" value={item.vat_rate} onChange={(e) => updateItem(index, 'vat_rate', e.target.value)} />
                        </div>
                      )}
                      <div className="col-span-1 flex items-end pb-2">
                        <Button type="button" variant="ghost" size="sm" onClick={() => removeItem(index)} disabled={items.length === 1}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Notes */}
            <Card className="border-slate-200">
              <CardHeader><CardTitle className="font-heading text-lg">Notes</CardTitle></CardHeader>
              <CardContent>
                <Textarea placeholder="Notes pour le client..." value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} data-testid="notes-input" />
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Validity */}
            <Card className="border-slate-200">
              <CardHeader><CardTitle className="font-heading text-lg">Validité</CardTitle></CardHeader>
              <CardContent>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start" data-testid="valid-until-btn">
                      <CalendarIcon className="w-4 h-4 mr-2" />
                      {validUntil ? format(validUntil, 'dd MMMM yyyy', { locale: fr }) : 'Sélectionner une date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0"><Calendar mode="single" selected={validUntil} onSelect={setValidUntil} /></PopoverContent>
                </Popover>
              </CardContent>
            </Card>

            {/* Summary */}
            <Card className="border-slate-200 bg-slate-50">
              <CardHeader><CardTitle className="font-heading text-lg">Récapitulatif</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Sous-total HT</span>
                  <span className="font-medium">{formatCurrency(calculateSubtotal())}</span>
                </div>
                {calculateDiscount() > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Remises</span>
                    <span className="font-medium text-red-600">-{formatCurrency(calculateDiscount())}</span>
                  </div>
                )}
                {vatEnabled && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">TVA</span>
                    <span className="font-medium">{formatCurrency(calculateVat())}</span>
                  </div>
                )}
                {!vatEnabled && (
                  <div className="text-xs text-amber-600 bg-amber-50 p-2 rounded border border-amber-200">
                    Sans TVA
                  </div>
                )}
                <div className="border-t border-slate-200 pt-3 flex justify-between">
                  <span className="font-semibold text-slate-900">Total {vatEnabled ? 'TTC' : ''}</span>
                  <span className="font-bold text-lg text-blue-600" data-testid="total">{formatCurrency(calculateTotal())}</span>
                </div>
              </CardContent>
            </Card>

            <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" disabled={saving || !clientId} data-testid="save-quote-btn">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Création...</> : <><Save className="w-4 h-4 mr-2" />Créer le devis</>}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default CreateQuotePage;
